import React from 'react';
import { ArrowLeft, RefreshCw, Shield, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { StatusBoxRow, type StatusBoxCounts } from '../../common/StatusBoxRow';
import PnlFlash from '../../common/PnlFlash';
import { formatCountdown } from '../../../utils/scheduleSummary';
import { INTRADAY_EXIT_MODE } from '../../../constants/tradingConstants';
import { CHIP_DENSE, CHIP_METADATA } from '../../../utils/chipColors';

export interface StrategyHeaderMetadata {
  underlying: string | null;
  expiryType: string | null;
  productType: string | null;
  tradingType: string | null;
  intradayExitMode: string | null;
}

export interface StrategyHeaderSchedule {
  scheduledEntryTime: string | null;
  scheduledExitTime: string | null;
  scheduledExitDate: string | null;
  linkedEntryDate: string | null;
  viewedDate: string;
  nowIstHms: string | null;
  brokerList: string | null;
  fillProgress: {
    filled: number;
    rejected: number;
    cancelled: number;
    pending: number;
    total: number;
    variant: 'green' | 'yellow' | 'red' | 'gray';
  };
  /** Pre-computed run-state chip — replaces "4/4 filled" with the canonical
   *  State chip shown in the Schedule table ("RUNNING 3/4", "ERROR 1/4", etc.).
   *  Absent when the strategy has no positions yet. */
  runState?: { label: string; variant: 'green' | 'red' | 'gray' };
}

export interface StrategyModalHeaderProps {
  basketName: string;
  strategyName: string;
  /** True when the parent basket is a marketplace subscription. Renders the
   *  basket label in violet + an MP pill next to it. */
  isMarketplace?: boolean;
  legCount: number;
  statusCounts: StatusBoxCounts;
  slCounts: { placed: number; rejected: number; pending: number };
  totalPnl: number;
  metadata: StrategyHeaderMetadata;
  /** When null, the schedule ribbon is omitted (Positions page path). */
  schedule: StrategyHeaderSchedule | null;
  onClose: () => void;
  onBack?: () => void;
  backLabel?: string;
  onRefresh?: () => void;
  refreshing?: boolean;
}

// Schedule-ribbon fill-progress pill. Bg + text from CHIP_DENSE (canonical
// palette); border classes are added per-variant because this surface uses
// a thin border in addition to the bg.
const FILL_CLS: Record<'green' | 'yellow' | 'red' | 'gray', string> = {
  green: `${CHIP_DENSE.COMPLETE} border-green-200 dark:border-green-800`,
  yellow: `${CHIP_DENSE.TRIGGER_PENDING} border-yellow-200 dark:border-yellow-800`,
  red: `${CHIP_DENSE.REJECTED} border-red-200 dark:border-red-800`,
  gray: `${CHIP_DENSE.CANCELLED} border-gray-200 dark:border-gray-700`,
};

function buildPills(m: StrategyHeaderMetadata): string[] {
  const pills: string[] = [];
  if (m.underlying) pills.push(m.underlying);
  if (m.expiryType) pills.push(m.expiryType.toUpperCase());
  if (m.tradingType) pills.push(m.tradingType);
  if (m.tradingType === 'INTRADAY' && m.intradayExitMode) {
    pills.push(m.intradayExitMode === INTRADAY_EXIT_MODE.NEXT_DAY_BTST ? 'BTST' : 'SAME DAY');
  }
  if (m.productType) pills.push(m.productType);
  return pills;
}

/**
 * Shared header + schedule ribbon for the strategy-level modal used by both
 * Classic and Pro schedule views and the Positions page. Owns identity
 * (basket label, strategy name, leg count, StatusBoxRow, metadata pills,
 * Total P&L, back/refresh/close) and — when `schedule` is supplied — a
 * ribbon with SL shield, entry → exit timing, countdown, broker list, and
 * fill-progress pill.
 */
export const StrategyModalHeader: React.FC<StrategyModalHeaderProps> = ({
  basketName, strategyName, isMarketplace = false,
  legCount, statusCounts, slCounts, totalPnl,
  metadata, schedule,
  onClose, onBack, backLabel = 'Back', onRefresh, refreshing = false,
}) => {
  const pills = buildPills(metadata);
  const slLive = slCounts.placed + slCounts.pending + slCounts.rejected;

  // Schedule-specific derivations
  let fillLabel: string | null = null;
  let fillVariant: 'green' | 'yellow' | 'red' | 'gray' = 'green';
  let timingLine: string | null = null;
  let countdownLine: string | null = null;
  if (schedule) {
    const fp = schedule.fillProgress;
    fillVariant = fp.variant;
    if (fp.total === 0) {
      fillLabel = 'no legs scheduled';
    } else if (fp.rejected > 0) {
      fillLabel = `${fp.rejected}/${fp.total} rejected`;
    } else if (fp.cancelled > 0) {
      fillLabel = `${fp.cancelled}/${fp.total} cancelled`;
    } else if (fp.pending > 0) {
      fillLabel = fp.filled > 0
        ? `${fp.filled}/${fp.total} filled · ${fp.pending} pending`
        : `${fp.pending}/${fp.total} pending`;
    } else {
      fillLabel = `${fp.filled}/${fp.total} filled`;
    }

    const isToday = !!schedule.nowIstHms;
    const isBtst = metadata.intradayExitMode === INTRADAY_EXIT_MODE.NEXT_DAY_BTST;
    const carryOverExit = !!schedule.linkedEntryDate && schedule.linkedEntryDate !== schedule.viewedDate;
    const entryCountdown = isToday && schedule.scheduledEntryTime
      ? formatCountdown(schedule.nowIstHms, schedule.scheduledEntryTime)
      : null;
    const exitOnDifferentDay = !!schedule.scheduledExitDate && schedule.scheduledExitDate !== schedule.viewedDate;
    const exitCountdown = isToday && schedule.scheduledExitTime && !exitOnDifferentDay
      ? formatCountdown(schedule.nowIstHms, schedule.scheduledExitTime)
      : null;

    if (schedule.scheduledEntryTime && schedule.scheduledExitTime) {
      if (isBtst) {
        // BTST cycles span two days: today's entry exits tomorrow, and today's
        // exit closes yesterday's entry.
        timingLine = carryOverExit
          ? `Exits ${schedule.scheduledExitTime} (from ${schedule.linkedEntryDate}) · New entry ${schedule.scheduledEntryTime} (exits next day)`
          : `Entry ${schedule.scheduledEntryTime} → Exit ${schedule.scheduledExitTime} (next day)`;
      } else {
        timingLine = `Entry ${schedule.scheduledEntryTime} → Exit ${schedule.scheduledExitTime}`;
      }
    } else if (schedule.scheduledEntryTime) {
      timingLine = `Enters at ${schedule.scheduledEntryTime}`;
    } else if (schedule.scheduledExitTime) {
      const linkedPrefix = schedule.linkedEntryDate && schedule.linkedEntryDate !== schedule.viewedDate
        ? `(entered ${schedule.linkedEntryDate}) `
        : '';
      timingLine = `${linkedPrefix}Exits at ${schedule.scheduledExitTime}`;
    }

    if (exitCountdown) {
      countdownLine = `Exits at ${schedule.scheduledExitTime} (${exitCountdown})`;
    } else if (entryCountdown) {
      countdownLine = `Enters at ${schedule.scheduledEntryTime} (in ${entryCountdown})`;
    }
  }

  return (
    <>
      {/* Identity block */}
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <div
              className={cn(
                'text-[10px] uppercase tracking-widest font-semibold truncate',
                isMarketplace
                  ? 'text-violet-700 dark:text-violet-300'
                  : 'text-gray-500 dark:text-gray-400',
              )}
            >
              {basketName || '—'}
            </div>
            {isMarketplace && (
              <span
                className={cn(
                  'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0',
                  CHIP_DENSE.MARKETPLACE,
                )}
                title="Marketplace subscription"
                aria-label="Marketplace subscription"
              >
                MP
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <h3
              className={cn(
                'text-base sm:text-lg font-bold truncate',
                isMarketplace
                  ? 'text-violet-700 dark:text-violet-300'
                  : 'text-gray-900 dark:text-white',
              )}
            >
              {strategyName || '—'}
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
              ({legCount} {legCount === 1 ? 'leg' : 'legs'})
            </span>
            <StatusBoxRow counts={statusCounts} />
          </div>
          {pills.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {pills.map((p) => (
                <span
                  key={p}
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider whitespace-nowrap',
                    p === 'BTST' ? CHIP_METADATA.BTST : CHIP_METADATA.DEFAULT,
                  )}
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total P&amp;L</div>
            <PnlFlash value={totalPnl} className="block text-right text-sm font-bold" />
          </div>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label={backLabel}
              title={backLabel}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{backLabel}</span>
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Schedule ribbon — only when schedule context is supplied */}
      {schedule && (
        <div className="px-5 py-2.5 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 flex-wrap text-xs">
          {/* State chip — "RUNNING 3/4", "ERROR 1/4", "CLOSED 4/4", etc.
              Replaces the old "4/4 filled" fill-progress label so the modal
              shows the same State chip as the Schedule table row. */}
          {schedule.runState ? (
            <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md font-semibold border', FILL_CLS[schedule.runState.variant])}>
              {schedule.runState.label}
            </span>
          ) : fillLabel ? (
            <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md font-semibold border', FILL_CLS[fillVariant])}>
              {fillLabel}
            </span>
          ) : null}
          {slLive > 0 && (
            <span className="flex items-center gap-1">
              <Shield className={cn(
                'w-3.5 h-3.5',
                slCounts.rejected > 0 ? 'text-red-500' : slCounts.pending > 0 ? 'text-yellow-500' : 'text-green-500',
              )} />
              <span className="text-gray-600 dark:text-gray-300 tabular-nums">
                SL {slCounts.placed}/{slLive}
                {slCounts.rejected > 0 && <span className="text-red-500 ml-1">· {slCounts.rejected} failed</span>}
                {slCounts.pending > 0 && slCounts.rejected === 0 && <span className="text-yellow-600 ml-1">· {slCounts.pending} pending</span>}
              </span>
            </span>
          )}
          {timingLine && (
            <span className="text-gray-600 dark:text-gray-300 tabular-nums">
              {timingLine}
            </span>
          )}
          {countdownLine && (
            <span className="inline-flex items-center gap-1 font-medium text-blue-700 dark:text-blue-300 tabular-nums">
              ⏱ {countdownLine}
            </span>
          )}
          {schedule.brokerList && (
            <span className="ml-auto text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[50%]" title={schedule.brokerList}>
              {schedule.brokerList}
            </span>
          )}
        </div>
      )}
    </>
  );
};

export default StrategyModalHeader;
